import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { ConfigService } from '../config.service';
import { UserService, UserData } from '../user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly TOKEN_KEY = 'accessToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private refreshInFlight: Promise<string | null> | null = null;

  private http = inject(HttpClient);
  private router = inject(Router);
  private configService = inject(ConfigService);
  private userService = inject(UserService);

  constructor() {}

  async login(usuario: string, password: string): Promise<boolean> {
    const url = `${this.configService.getCurrentApiUrl()}/login`;

    try {
      const response: Partial<UserData> = await lastValueFrom(
        this.http.post<Partial<UserData>>(
          url,
          { usuario, password },
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
          }
        )
      );

      if (response && response.accessToken && response.refreshToken) {
        this.saveTokens(response.accessToken, response.refreshToken);
        
        // Guardar los datos del usuario en el UserService
        if (response.codigo) {
          const userData: UserData = {
            descricao: response.descricao || '',
            codigo: response.codigo,
            unidade: response.unidade || 0,
            tipo: response.tipo || 0,
            empresa: response.empresa || 0,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            CLIENTES: response.CLIENTES || null,
            VENDEDORES: response.VENDEDORES || null,
            CUENTA_CORRIENTE: response.CUENTA_CORRIENTE || null,
            LISTA_PRECIO: response.LISTA_PRECIO || null,
            METAS: response.METAS || null,
            PEDIDOS: response.PEDIDOS || null,
            STOCK: response.STOCK || null,
            vendedorCod: response.vendedorCod || null,
            pedidosTodos: response.pedidosTodos
          };
          this.userService.setUserData(userData);
        }
        
        return true;
      } else {
        console.error('Resposta de login inválida:', response);
        return false;
      }
    } catch (error: unknown) {
      const e = error as HttpErrorResponse;
      console.error('Erro na requisição de login:', e?.status ?? e?.message);
      throw error; // propaga o HttpErrorResponse original (preserva .status)
    }
  }

  async refreshToken(): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    // Evita refrescos simultáneos (p.ej. varios 401 a la vez)
    this.refreshInFlight ??= this.doRefreshToken(refreshToken).finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }

  private async doRefreshToken(
    refreshToken: string,
  ): Promise<string | null> {
    const url = `${this.configService.getCurrentApiUrl()}/token/refresh`;

    try {
      const response: { accessToken?: string } = await lastValueFrom(
        this.http.post<{ accessToken?: string }>(url, { refreshToken })
      );
      if (response && response.accessToken) {
        this.saveAccessToken(response.accessToken);
        return response.accessToken;
      } else {
        this.logout();
        return null;
      }
    } catch (error) {
      console.error('Erro ao refrescar o token:', error);
      this.logout();
      return null;
    }
  }

  /**
   * Indica si el access token existe y no está vencido.
   */
  hasValidAccessToken(): boolean {
    const token = this.getAccessToken();
    return !!token && !this.isTokenExpired(token);
  }

  /**
   * Garantiza una sesión utilizable: devuelve true si hay un access token
   * vigente (o renovable con el refresh token) y datos de usuario cargados.
   * Si quedan tokens huérfanos sin datos de usuario, limpia la sesión.
   */
  async ensureAuthenticated(): Promise<boolean> {
    if (this.hasValidAccessToken() && this.userService.getCurrentUser()) {
      await this.refreshUserDataIfStale();
      return true;
    }
    if (!this.getRefreshToken()) {
      this.clearSession();
      return false;
    }
    const renewed = (await this.refreshToken()) !== null;
    const hasUser = !!this.userService.getCurrentUser();
    if (!renewed || !hasUser) {
      this.clearSession();
      return false;
    }
    await this.refreshUserDataIfStale();
    return true;
  }

  /**
   * Refresca los datos del usuario desde GET /api/me cuando la sesión
   * guardada es de antes de campos nuevos (pedidosTodos, STOCK). El
   * backend recalcula los permisos en cada request, así que una sola
   * llamada deja la sesión al día sin re-login.
   */
  private async refreshUserDataIfStale(): Promise<void> {
    const user = this.userService.getCurrentUser();
    if (!user || (user.pedidosTodos !== undefined && user.STOCK !== undefined)) {
      return;
    }
    try {
      const url = `${this.configService.getCurrentApiUrl()}/me`;
      const response = await lastValueFrom(
        this.http.get<{ success?: boolean; data?: Partial<UserData> }>(url)
      );
      const data = response?.data;
      if (!data?.codigo) return;
      this.userService.setUserData({ ...user, ...data });
    } catch {
      // Si falla el refresh, se mantiene la sesión guardada tal cual.
    }
  }

  /** Limpia tokens y datos de usuario sin navegar. */
  private clearSession(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    this.userService.logout();
  }

  /**
   * Decodifica el payload del JWT y compara `exp` contra la hora actual.
   * Devuelve true si está vencido o no se puede leer.
   */
  private isTokenExpired(token: string): boolean {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      const exp = payload?.exp;
      if (typeof exp !== 'number') {
        return false;
      }
      // Margen de 15s para evitar expirar en el medio de una request
      return exp * 1000 <= Date.now() + 15000;
    } catch {
      return true;
    }
  }

  saveTokens(accessToken: string, refreshToken: string) {
    localStorage.setItem(this.TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  saveAccessToken(accessToken: string) {
    localStorage.setItem(this.TOKEN_KEY, accessToken);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  logout() {
    this.clearSession();
    this.router.navigateByUrl('/login', { replaceUrl: true });
  }

  // Busca a descrição do usuário pelo código (para autocomplete no login)
  async buscarUsuarioPorCodigo(codigo: number): Promise<{ codigo: number; descricao: string } | null> {
    const url = `${this.configService.getCurrentApiUrl()}/login/usuario/${codigo}`;
    try {
      const response: { codigo?: number; descricao?: string } = await lastValueFrom(
        this.http.get<{ codigo?: number; descricao?: string }>(url, {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        })
      );
      return response && response.codigo && response.descricao
        ? { codigo: response.codigo, descricao: response.descricao }
        : null;
    } catch (error: unknown) {
      // 404 = usuário não encontrado; outros = erro de conexão
      if ((error as HttpErrorResponse)?.status === 404) return null;
      throw error;
    }
  }
}